import {
  InventoryItemPriceType as InventoryItemPriceTypeProto,
  InventoryItem as InventoryItemProto,
  InventoryItemDocumentSignature,
  InventoryItemSupportingItem,
  InventoryItemTypeSpecification as InventoryItemTypeSpecificationProto,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import {
  DocumentSignatureItemDto,
  InventoryItemDto,
  InventoryItemPriceType,
  SupportingItemDto,
  SupportingItemSpecDto,
  SupportingItemSpecTypesDto,
} from './inventory-item'
import { timestampToISO, decimalToNumber } from '../../utils/grpc-util'
import { BadRequest } from 'fejl'
import { match } from 'utils/pattern-match-util'
import {
  InventoryItemTypeSpecification,
  InventoryItemDefaultTypeSpecification,
  InventoryItemBookkeepingTypeSpecification,
  InventoryItemConsultationTypeSpecification,
} from 'admin/inventory/InventoryItem'

export function mapInventoryItemProtoToDTO(
  proto: InventoryItemProto
): InventoryItemDto {
  const price = proto.getPrice()
  const proPrice = proto.getProPrice()

  return {
    id: proto.getId(),
    version_id: proto.getVersionId(),
    short_id: proto.getShortId(),
    name: proto.getName(),
    description: proto.getDescription(),
    workspace_id: proto.getWorkspaceId() || undefined,
    archived: proto.getArchived(),
    pro_visibility: proto.getProVisibility(),
    created_by: proto.getCreatedBy(),
    version: proto.getVersion(),
    date_created: timestampToISO(proto.getDateCreated()),
    courtesy_amount: proto.getCourtesyAmount(),
    price: price ? decimalToNumber(price) : 0,
    pro_price: proPrice ? decimalToNumber(proPrice) : 0,
    root_id: proto.getRootId() || undefined,
    price_type: mapInventoryItemPriceTypeProtoToDTO(proto.getPriceType()),
    supporting_items: proto
      .getSupportingItemsList()
      .map(mapSupportingItemProtoToDTO),
    document_signatures: proto
      .getDocumentSignaturesList()
      .map(mapDocumentSignatureItemProtoToDTO),
    type_specification: mapTypeSpecificationProtoToDTO(
      proto.getTypeSpecification()
    ),
  }
}

function mapTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto
): InventoryItemTypeSpecification {
  if (!spec) {
    throw new BadRequest('Type specification not found')
  }

  switch (spec.getSpec()?.getTypeCase()) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.DEFAULT:
      return new InventoryItemDefaultTypeSpecification()
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.BOOKKEEPING:
      return new InventoryItemBookkeepingTypeSpecification()
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.CONSULTATION:
      return mapConsultationTypeSpecificationProtoToDTO(
        spec.getSpec()?.getConsultation()
      )
    default:
      throw new BadRequest('Unsupported type specification')
  }
}

function mapConsultationTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto.TypeOptions.Consultation
): InventoryItemConsultationTypeSpecification {
  if (!spec) {
    throw new BadRequest('Consultation type specification not found')
  }

  return new InventoryItemConsultationTypeSpecification(
    spec.getLengthInMinutes()
  )
}

function mapInventoryItemPriceTypeProtoToDTO(
  priceType: InventoryItemPriceTypeProto
): InventoryItemPriceType {
  return match(priceType, {
    [InventoryItemPriceTypeProto.FEE]: () => InventoryItemPriceType.FEE,
    [InventoryItemPriceTypeProto.FLAT]: () => InventoryItemPriceType.FLAT,
    [InventoryItemPriceTypeProto.MARKUP]: () => InventoryItemPriceType.MARKUP,
    [InventoryItemPriceTypeProto.MARKDOWN]: () =>
      InventoryItemPriceType.MARKDOWN,
  })
}

function mapSupportingItemProtoToDTO(
  supportingItemProto: InventoryItemSupportingItem
): SupportingItemDto {
  return {
    required: supportingItemProto.getRequired(),
    spec: mapSupportingItemSpecProtoToDTO(supportingItemProto.getSpec()),
  }
}

function mapSupportingItemSpecProtoToDTO(
  specProto?: InventoryItemSupportingItem.Spec
): SupportingItemSpecDto {
  if (!specProto) {
    throw new BadRequest('No supporting item spec type found')
  }

  switch (specProto.getTypeCase()) {
    case InventoryItemSupportingItem.Spec.TypeCase.DOCUMENT:
      return mapSupportingItemDocumentSpecProtoToDTO(specProto.getDocument())
    default:
      throw new BadRequest('Unsupported supporting item spec type')
  }
}

function mapSupportingItemDocumentSpecProtoToDTO(
  documentSpecProto?: InventoryItemSupportingItem.Spec.Document
): SupportingItemSpecDto {
  if (!documentSpecProto) {
    throw new BadRequest('Supporting item spec document not found')
  }

  return {
    type: SupportingItemSpecTypesDto.DOCUMENT,
    document: {
      type_short_id: documentSpecProto.getTypeShortId(),
    },
  }
}

function mapDocumentSignatureItemProtoToDTO(
  documentSignatureProto: InventoryItemDocumentSignature
): DocumentSignatureItemDto {
  return {
    document_type_short_id: documentSignatureProto.getDocumentTypeShortId(),
  }
}
